<template>
  <div class="container">

    <h1 class="se-bp-title">
      Input Data: {{ getWageEmploymentLabel(employmentIdx) }} — {{ getWageIncomeDocumentLabel(employmentIdx, docIdx) }}
    </h1>

    <form autocomplete="off" v-if="currentIncomeDocument">

      <!-- ================= Income Document Header ================= -->
      <h3 class="mt-4">Document Details</h3>

      <div class="row" v-if="isW2">
        <div class="col-md-3" :class="{ 'se-field-missing': missingFields.includes('Year') }">
          <label class="bp-label">Year *</label>
          <el-date-picker size="large" v-model="year" type="year" value-format="YYYY" :disabled="isLocked" clearable placeholder="Pick a year"/>
        </div>
        <div class="col-md-4" :class="{ 'se-field-missing': missingFields.includes(totalWagesLabel) }">
          <label class="bp-label">{{ totalWagesLabel }}</label>
          <input class="bp-form-input bp-u-shadow-04" type="text" :readonly="isLocked" v-mask="currencyMask" v-model="totalWages"/>
        </div>
      </div>

      <!-- ================= Paystub Header Fields ================= -->
      <div class="row" v-if="!isW2">
        <div class="col" :class="{ 'se-field-missing': missingFields.includes('Year') }">
          <label class="bp-label">Year *</label>
          <el-date-picker size="large" v-model="year" type="year" value-format="YYYY" :disabled="isLocked" clearable placeholder="Pick a year"/>
        </div>
        <div class="col" :class="{ 'se-field-missing': missingFields.includes('Pay date') }">
          <label class="bp-label">Pay date *</label>
          <el-date-picker size="large" v-model="payDate" type="date" value-format="MM/DD/YYYY" format="MM/DD/YYYY" :disabled="isLocked" clearable placeholder="Select date"/>
        </div>
        <div class="col" :class="{ 'se-field-missing': missingFields.includes('Period start') }">
          <label class="bp-label">Period start <span v-if="!periodStartNotOnPaystub">*</span></label>
          <el-date-picker size="large" v-model="payPeriodStartDate" type="date" value-format="MM/DD/YYYY" format="MM/DD/YYYY" :disabled="isLocked || periodStartNotOnPaystub" clearable placeholder="Select date"/>
          <el-checkbox class="se-not-on-paystub-cb" v-model="periodStartNotOnPaystub" :disabled="isLocked">This date is not on the paystub</el-checkbox>
        </div>
        <div class="col" :class="{ 'se-field-missing': missingFields.includes('Period end') }">
          <label class="bp-label">Period end <span v-if="!periodEndNotOnPaystub">*</span></label>
          <el-date-picker size="large" v-model="payPeriodEndDate" type="date" value-format="MM/DD/YYYY" format="MM/DD/YYYY" :disabled="isLocked || periodEndNotOnPaystub" clearable placeholder="Select date"/>
          <el-checkbox class="se-not-on-paystub-cb" v-model="periodEndNotOnPaystub" :disabled="isLocked">This date is not on the paystub</el-checkbox>
        </div>
        <div class="col">
          <label class="bp-label">{{ totalWagesLabel }}</label>
          <input class="bp-form-input bp-u-shadow-04 se-readonly-input" type="text" readonly :value="formatCurrency(incomeItemsTotalPayAmount)"/>
        </div>
      </div>


      <!-- ================= Income Items ================= -->
      <template v-if="!isW2">
        <h3 class="mt-5">Earnings breakdown</h3>

        <el-collapse v-model="activePanels" accordion>
          <el-collapse-item v-for="(item, idx) in currentIncomeDocument.incomeItems" :key="idx" :name="idx">
            <template #title>
              <div class="se-earnings-header-row">
                <div class="se-earnings-title-block">
                  <div class="se-panel-title">{{ itemTitle(item) }}</div>
                  <div class="se-panel-subtitle">This period {{ formatCurrency(item.payPeriodAmount) }} | {{ payTotalAmountLabel }} {{ formatCurrency(item.payTotalAmount) }}</div>
                </div>
                <button class="bp-button bp-button--destructive bp-button--small se-earnings-delete-btn" :disabled="isLocked" @click.stop.prevent="removeIncomeItem(idx)">Delete</button>
                <span class="se-earnings-status" :class="isItemComplete(item) ? 'se-earnings-status--complete' : 'se-earnings-status--incomplete'">
                  {{ isItemComplete(item) ? 'Completed' : 'Incomplete' }}
                </span>
              </div>
            </template>

            <div class="row mt-2">
              <div class="col-md-6 mt-2">
                <fg-input label="Description" type="text" :readonly="isLocked" :model-value="item.description" @update:model-value="v => onDescriptionChange(idx, v)"></fg-input>
              </div>
              <div class="col-md-6 mt-2">
                <label class="bp-label">Category *</label>
                <el-select size="large" class="se-category-select" :class="{ 'se-category-flash': flashingCategoryIndices[idx] }" :model-value="item.category" @update:model-value="v => setItemField(idx, 'category', v)" :disabled="isLocked" clearable placeholder="Select">
                  <el-option v-for="opt in categoryOptions" :key="opt.value" :label="opt.label" :value="opt.value"/>
                </el-select>
              </div>
              <div class="col-md-3 mt-2">
                <fg-input label="Hours" type="number" :readonly="isLocked" :model-value="item.hoursCount" @update:model-value="v => setItemField(idx, 'hoursCount', Number(v))"></fg-input>
              </div>
              <div class="col-md-3 mt-2">
                <label class="bp-label">Hourly Rate of Pay</label>
                <input class="bp-form-input bp-u-shadow-04" type="text" :readonly="isLocked" v-mask="currencyMask" :value="item.hourlyRateOfPay" @change="setItemField(idx, 'hourlyRateOfPay', parseCurrencyToNumber($event.target.value))"/>
              </div>
              <div class="col-md-3 mt-2">
                <label class="bp-label">Pay Period Amount</label>
                <input class="bp-form-input bp-u-shadow-04" type="text" :readonly="isLocked" v-mask="currencyMask" :value="item.payPeriodAmount" @change="setItemField(idx, 'payPeriodAmount', parseCurrencyToNumber($event.target.value))"/>
              </div>
              <div class="col-md-3 mt-2">
                <label class="bp-label">Pay Total Amount</label>
                <input class="bp-form-input bp-u-shadow-04" type="text" :readonly="isLocked" v-mask="currencyMask" :value="item.payTotalAmount" @change="setItemField(idx, 'payTotalAmount', parseCurrencyToNumber($event.target.value))"/>
              </div>
            </div>
          </el-collapse-item>
        </el-collapse>

        <div class="se-earnings-add-row">
          <button class="bp-button bp-button--primary" :disabled="isLocked" @click.prevent="addEarningsLine">Add earnings line</button>
        </div>
      </template>

    </form>

    <se-buttons
      :display-previous="true"
      :display-save="false"
      :display-submit="false"
      @seNext="next"
      @sePrevious="previous">
      <template #extra v-if="isW2">
        <span class="bp-u-shadow-08" style="margin-left: 4rem;">
          <button class="bp-button bp-button--primary" :disabled="isLocked" @click.prevent="addAnotherW2">Add another W-2</button>
        </span>
        <span class="ml-2 bp-u-shadow-08">
          <button class="bp-button bp-button--destructive" :disabled="isLocked" @click.prevent="deleteW2">Delete this W-2</button>
        </span>
      </template>
      <template #extra v-else-if="isYtdPaystub">
        <span class="bp-u-shadow-08" style="margin-left: 4rem;">
          <button class="bp-button bp-button--primary" :disabled="isLocked" @click.prevent="addAnotherYtdPaystub">Add another YTD Paystub</button>
        </span>
        <span class="ml-2 bp-u-shadow-08">
          <button class="bp-button bp-button--destructive" :disabled="isLocked" @click.prevent="deleteYtdPaystub">Delete this YTD Paystub</button>
        </span>
      </template>
      <template #extra v-else-if="isPriorYearPaystub">
        <span class="bp-u-shadow-08" style="margin-left: 4rem;">
          <button class="bp-button bp-button--destructive" :disabled="isLocked" @click.prevent="deletePriorYearPaystub">Delete this Prior Year Paystub</button>
        </span>
      </template>
    </se-buttons>

  </div>
</template>
<script>
import { defineComponent } from 'vue';
import {ElSelect, ElOption, ElDatePicker, ElCollapse, ElCollapseItem, ElCheckbox} from 'element-plus'
import Swal from 'sweetalert2/dist/sweetalert2'
import SeButtons from './SeButtons.vue'
import wageFormMixin from '@/mixins/WageFormMixin'
import Enums from 'src/model/enums'
import seapi from '@/util/seapi'
import {
  SET_WAGE_INCOME_DOCUMENT_DATA,
  ADD_WAGE_INCOME_DOCUMENT, DELETE_WAGE_INCOME_DOCUMENT,
  ADD_WAGE_INCOME_ITEM, SET_WAGE_INCOME_ITEM, DELETE_WAGE_INCOME_ITEM
} from '@/store/actions/se'

function toOptions(valuesEnum, labelsEnum) {
  return Object.keys(valuesEnum).map(key => ({value: valuesEnum[key], label: labelsEnum[key]}))
}

export default defineComponent({
  mixins: [wageFormMixin],

  components: {
    SeButtons,
    [ElSelect.name]: ElSelect,
    [ElOption.name]: ElOption,
    [ElDatePicker.name]: ElDatePicker,
    [ElCollapse.name]: ElCollapse,
    [ElCollapseItem.name]: ElCollapseItem,
    [ElCheckbox.name]: ElCheckbox
  },

  data() {
    return {
      activePanels: null,
      missingFields: [],
      periodStartNotOnPaystub: false,
      periodEndNotOnPaystub: false,

      documentationTypeOptions: toOptions(Enums.SeWageDocumentationType, Enums.SeWageDocumentationTypeLabel),
      documentationSourceOptions: toOptions(Enums.SeWageDocumentationSourceType, Enums.SeWageDocumentationSourceTypeLabel),
      categoryOptions: toOptions(Enums.SeWageIncomeItemCategoryType, Enums.SeWageIncomeItemCategoryTypeLabel),

      // Keyed by income item index; true while the "category auto-set by lookup"
      // flash animation should be showing on that line's Category dropdown.
      flashingCategoryIndices: {}
    }
  },

  created() {
    this.initNotOnPaystubCheckboxes()
    // Non-reactive bookkeeping for the debounced "guess category from description"
    // lookup (keyed by income item index). Kept off the reactive `data()` object
    // since they're plumbing, not view state.
    this.categoryLookupTimers = {}
    this.categoryLookupCache = {}
  },

  beforeUnmount() {
    Object.values(this.categoryLookupTimers).forEach(timer => clearTimeout(timer))
    Object.values(this.categoryFlashTimers || {}).forEach(timer => clearTimeout(timer))
  },

  computed: {
    year: { get() { return this.currentIncomeDocument?.year }, set(v) { this.setDocField('year', v) } },
    wageDocumentation: { get() { return this.currentIncomeDocument?.wageDocumentation }, set(v) { this.setDocField('wageDocumentation', v) } },
    wageDocumentationSource: { get() { return this.currentIncomeDocument?.wageDocumentationSource }, set(v) { this.setDocField('wageDocumentationSource', v) } },
    wageDocumentationDate: { get() { return this.currentIncomeDocument?.wageDocumentationDate }, set(v) { this.setDocField('wageDocumentationDate', v) } },
    payDate: { get() { return this.currentIncomeDocument?.payDate }, set(v) { this.setDocFields({payDate: v, wageDocumentationDate: v}) } },
    payPeriodStartDate: { get() { return this.currentIncomeDocument?.payPeriodStartDate }, set(v) { this.setDocField('payPeriodStartDate', v) } },
    payPeriodEndDate: { get() { return this.currentIncomeDocument?.payPeriodEndDate }, set(v) { this.setDocField('payPeriodEndDate', v) } },
    totalWages: { get() { return this.currentIncomeDocument?.totalWages }, set(v) { this.setDocField('totalWages', this.parseCurrencyToNumber(v)) } },
    isW2() { return this.wageDocumentation === Enums.SeWageDocumentationType.w2Form },
    isYtdPaystub() { return this.wageDocumentation === Enums.SeWageDocumentationType.yearToDatePayStub },
    isPriorYearPaystub() { return this.wageDocumentation === Enums.SeWageDocumentationType.yearEndPayStub },
    totalWagesLabel() {
      if (this.isW2) return 'W-2 Box 5 - Medicare wages and tips *'
      return this.isPriorYearPaystub ? 'Total Year-End Wages' : 'Total Year-to-Date Wages'
    },
    payTotalAmountLabel() { return this.isPriorYearPaystub ? 'Year End Total' : 'YTD' },
    incomeItemsTotalPayAmount() {
      return (this.currentIncomeDocument?.incomeItems || [])
        .reduce((sum, item) => sum + (Number(item.payTotalAmount) || 0), 0)
    }
  },

  watch: {
    year(newYear) {
      if (newYear) this.missingFields = this.missingFields.filter(f => f !== 'Year')
      ;(this.currentIncomeDocument?.incomeItems || []).forEach((item, idx) => {
        if (item.year !== newYear) {
          this.$store.commit(SET_WAGE_INCOME_ITEM, {employmentIdx: this.employmentIdx, docIdx: this.docIdx, itemIdx: idx, fields: {year: newYear}})
        }
      })
    },

    payDate(v) { if (v) this.missingFields = this.missingFields.filter(f => f !== 'Pay date') },
    payPeriodStartDate(v) { if (v) this.missingFields = this.missingFields.filter(f => f !== 'Period start') },
    payPeriodEndDate(v) { if (v) this.missingFields = this.missingFields.filter(f => f !== 'Period end') },
    totalWages(v) { if (v !== null && v !== undefined && v !== '') this.missingFields = this.missingFields.filter(f => f !== this.totalWagesLabel) },

    periodStartNotOnPaystub(checked) {
      if (checked) {
        this.payPeriodStartDate = null
        this.missingFields = this.missingFields.filter(f => f !== 'Period start')
      }
    },

    periodEndNotOnPaystub(checked) {
      if (checked) {
        this.payPeriodEndDate = null
        this.missingFields = this.missingFields.filter(f => f !== 'Period end')
      }
    },

    incomeItemsTotalPayAmount: {
      immediate: true,
      handler(newTotal) {
        if (!this.isW2 && this.currentIncomeDocument && this.currentIncomeDocument.totalWages !== newTotal) {
          this.setDocField('totalWages', newTotal)
        }
      }
    },

    // Vue Router reuses this component instance when navigating between two routes that
    // resolve to it (e.g. /wage-income-document/0/0 -> /wage-income-document/0/1), so
    // created() only fires once. Re-run the (re-)initialization whenever the document
    // being edited changes.
    '$route.params'() {
      this.missingFields = []
      this.periodStartNotOnPaystub = false
      this.periodEndNotOnPaystub = false
      this.initNotOnPaystubCheckboxes()
    }
  },

  methods: {
    // Override the generic WageFormMixin `next()` so we can validate that every
    // earnings line has a Category selected before allowing navigation onward.
    next() {
      if (!this.validateHeaderFields()) return
      if (!this.validateIncomeItemsCategories()) return
      this.saveRequest();
      let routes = this.getWageFormRoutes
      let ri = routes.indexOf(this.$route.path);
      if (ri !== -1 && ri < routes.length - 1) {
        this.$router.push(routes[ri + 1])
      } else {
        this.$router.push('/se-eval/eval-findings')
      }
    },

    // Ensures the required header fields are populated: for W2 documents that's just
    // Year and Total Wages (W-2 Box 5); for paystubs it's Year, Pay date, Period start
    // and Period end. Highlights the empty fields and shows a popup message on failure.
    validateHeaderFields() {
      let missing = []
      if (this.isW2) {
        if (!this.year) missing.push('Year')
        if (this.totalWages === null || this.totalWages === undefined || this.totalWages === '') missing.push(this.totalWagesLabel)
      } else {
        if (!this.year) missing.push('Year')
        if (!this.payDate) missing.push('Pay date')
        if (!this.payPeriodStartDate && !this.periodStartNotOnPaystub) missing.push('Period start')
        if (!this.payPeriodEndDate && !this.periodEndNotOnPaystub) missing.push('Period end')
      }
      this.missingFields = missing
      if (missing.length > 0) {
        Swal.fire({
          title: 'Required fields missing',
          html: `Please complete the following required field(s): <b>${missing.join(', ')}</b>.`,
          icon: 'warning',
          buttonsStyling: false,
          customClass: {confirmButton: 'bp-button bp-button--primary'}
        })
        return false
      }
      return true
    },

    // Ensures every existing income item has a Category selected. Expands the first
    // offending panel and shows a popup message when validation fails.
    validateIncomeItemsCategories() {
      let items = this.currentIncomeDocument?.incomeItems || []
      let missingIdx = items.findIndex(item => !item.category)
      if (missingIdx !== -1) {
        this.activePanels = missingIdx
        Swal.fire({
          title: 'Category required',
          html: 'Please select a Category for each earnings line before continuing.',
          icon: 'warning',
          buttonsStyling: false,
          customClass: {confirmButton: 'bp-button bp-button--primary'}
        })
        return false
      }
      return true
    },

    setDocField(key, value) {
      this.setDocFields({[key]: value})
    },

    setDocFields(fields) {
      this.$store.commit(SET_WAGE_INCOME_DOCUMENT_DATA, {employmentIdx: this.employmentIdx, docIdx: this.docIdx, fields})
    },

    // "This date is not on the paystub" isn't part of the API model, so it can't be persisted/restored
    // directly. Instead, infer it on load: if this looks like a previously-entered paystub (has some
    // data already, e.g. a Year), and a period date is empty, assume the user had indicated that date
    // wasn't on the paystub rather than simply not having reached this page yet.
    initNotOnPaystubCheckboxes() {
      let doc = this.currentIncomeDocument
      if (!doc || this.isW2) return
      let hasExistingData = !!doc.year
      if (hasExistingData) {
        this.periodStartNotOnPaystub = !doc.payPeriodStartDate
        this.periodEndNotOnPaystub = !doc.payPeriodEndDate
      }
    },

    categoryLabel(v) { return Enums.SeWageIncomeItemCategoryTypeLabel[v] || v },

    addAnotherW2() {
      this.$store.commit(ADD_WAGE_INCOME_DOCUMENT, {employmentIdx: this.employmentIdx})
      let newIdx = this.currentEmployment.incomeDocuments.length - 1
      this.$store.commit(SET_WAGE_INCOME_DOCUMENT_DATA, {employmentIdx: this.employmentIdx, docIdx: newIdx, fields: {wageDocumentation: Enums.SeWageDocumentationType.w2Form}})
      this.saveRequest()
      this.$router.push(`/se-eval/wage-income-document/${this.employmentIdx}/${newIdx}`)
    },

    deleteW2() {
      this.confirmDelete('This W2, including all income items, will be permanently removed.', () => {
        this.$store.commit(DELETE_WAGE_INCOME_DOCUMENT, {employmentIdx: this.employmentIdx, docIdx: this.docIdx})
        this.saveRequest()
        this.$router.push(`/se-eval/wage-employment-docs/${this.employmentIdx}`)
      })
    },

    addAnotherYtdPaystub() {
      this.$store.commit(ADD_WAGE_INCOME_DOCUMENT, {employmentIdx: this.employmentIdx})
      let newIdx = this.currentEmployment.incomeDocuments.length - 1
      this.$store.commit(SET_WAGE_INCOME_DOCUMENT_DATA, {employmentIdx: this.employmentIdx, docIdx: newIdx, fields: {wageDocumentation: Enums.SeWageDocumentationType.yearToDatePayStub}})
      this.saveRequest()
      this.$router.push(`/se-eval/wage-income-document/${this.employmentIdx}/${newIdx}`)
    },

    deleteYtdPaystub() {
      this.confirmDelete('This YTD Paystub, including all income items, will be permanently removed.', () => {
        this.$store.commit(DELETE_WAGE_INCOME_DOCUMENT, {employmentIdx: this.employmentIdx, docIdx: this.docIdx})
        this.saveRequest()
        this.$router.push(`/se-eval/wage-employment-docs/${this.employmentIdx}`)
      })
    },


    deletePriorYearPaystub() {
      this.confirmDelete('This Prior Year Paystub, including all income items, will be permanently removed.', () => {
        this.$store.commit(DELETE_WAGE_INCOME_DOCUMENT, {employmentIdx: this.employmentIdx, docIdx: this.docIdx})
        this.saveRequest()
        this.$router.push(`/se-eval/wage-employment-docs/${this.employmentIdx}`)
      })
    },

    addEarningsLine() {
      if (!this.validateIncomeItemsCategories()) return
      this.$store.commit(ADD_WAGE_INCOME_ITEM, {employmentIdx: this.employmentIdx, docIdx: this.docIdx})
      let newIdx = this.currentIncomeDocument.incomeItems.length - 1
      this.$store.commit(SET_WAGE_INCOME_ITEM, {employmentIdx: this.employmentIdx, docIdx: this.docIdx, itemIdx: newIdx, fields: {year: this.year}})
      this.activePanels = newIdx
      this.saveRequest()
    },

    setItemField(idx, key, value) {
      let currencyFields = ['hourlyRateOfPay', 'payPeriodAmount', 'payTotalAmount']
      let fields = {[key]: currencyFields.includes(key) ? this.parseCurrencyToNumber(value) : value}
      this.$store.commit(SET_WAGE_INCOME_ITEM, {employmentIdx: this.employmentIdx, docIdx: this.docIdx, itemIdx: idx, fields: fields})
    },

    // Updates the description field as the user types, and (re)schedules a debounced
    // call to the category-guessing API so we don't fire a request on every keystroke.
    onDescriptionChange(idx, value) {
      this.setItemField(idx, 'description', value)
      this.scheduleCategoryLookup(idx, value)
    },

    // Waits for a short pause in typing before calling the category-guess API.
    // Restarting the timer on every keystroke means the API is only ever called
    // once the user has "finished typing" the description (or paused for a beat).
    scheduleCategoryLookup(idx, desc) {
      if (this.categoryLookupTimers[idx]) {
        clearTimeout(this.categoryLookupTimers[idx])
      }
      this.categoryLookupTimers[idx] = setTimeout(() => {
        delete this.categoryLookupTimers[idx]
        this.lookupCategoryForItem(idx, desc)
      }, 700)
    },

    // Calls the category-guess API for the given (already debounced) description and,
    // if a recognized category is returned, sets it on the earnings line's Category
    // dropdown. Results are cached per item index so that if the description hasn't
    // changed since the last successful lookup we don't call the API again.
    async lookupCategoryForItem(idx, desc) {
      let trimmed = (desc || '').trim()
      if (!trimmed) return

      let item = this.currentIncomeDocument?.incomeItems?.[idx]
      if (!item) return

      let cached = this.categoryLookupCache[idx]
      if (cached && cached.desc === trimmed) {
        // Already called the API for this exact description (found or not found) —
        // nothing new to look up.
        return
      }

      try {
        let result = await seapi.getIncomeCategory(trimmed)
        let categoryType = result?.categoryType

        // Guard against the description having changed again while the request
        // was in flight (e.g. user kept typing during a slow response).
        let currentItem = this.currentIncomeDocument?.incomeItems?.[idx]
        if (!currentItem || (currentItem.description || '').trim() !== trimmed) return

        this.categoryLookupCache[idx] = {desc: trimmed, found: !!categoryType}

        if (categoryType && Enums.SeWageIncomeItemCategoryType[categoryType]) {
          this.setItemField(idx, 'category', categoryType)
          this.flashCategory(idx)
        }
      } catch (e) {
        // Network/server error other than 404 — don't cache, so we retry the
        // next time the user pauses typing (or on next attempt).
        console.warn('Category lookup failed', e)
      }
    },

    // Briefly highlights the Category dropdown to draw the user's attention to the
    // fact that its value was just auto-populated by the description lookup.
    flashCategory(idx) {
      if (this.categoryFlashTimers && this.categoryFlashTimers[idx]) {
        clearTimeout(this.categoryFlashTimers[idx])
      }
      this.categoryFlashTimers = this.categoryFlashTimers || {}
      // Toggle off first (in case it's already true) so the CSS animation restarts
      // if two lookups resolve to the same item in quick succession.
      this.flashingCategoryIndices[idx] = false
      this.$nextTick(() => {
        this.flashingCategoryIndices[idx] = true
        this.categoryFlashTimers[idx] = setTimeout(() => {
          this.flashingCategoryIndices[idx] = false
        }, 1400)
      })
    },

    itemTitle(item) {
      let parts = [item.description, this.categoryLabel(item.category)].filter(v => v !== null && v !== undefined && v !== '')
      return parts.length > 0 ? parts.join(' - ') : 'New Earnings Line'
    },

    isItemComplete(item) {
      return !!(item.category && item.payPeriodAmount !== null && item.payPeriodAmount !== undefined
        && item.payTotalAmount !== null && item.payTotalAmount !== undefined)
    },

    formatCurrency(v) {
      if (v === null || v === undefined || v === '') return '0.00'
      return Number(v).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})
    },

    removeIncomeItem(idx) {
      this.confirmDelete('This income item will be permanently removed.', () => {
        this.$store.commit(DELETE_WAGE_INCOME_ITEM, {employmentIdx: this.employmentIdx, docIdx: this.docIdx, itemIdx: idx})
        // Item indices shift after a delete, so drop any pending timers/cache
        // entries keyed by index rather than risk them applying to the wrong line.
        Object.values(this.categoryLookupTimers).forEach(timer => clearTimeout(timer))
        this.categoryLookupTimers = {}
        this.categoryLookupCache = {}
        Object.values(this.categoryFlashTimers || {}).forEach(timer => clearTimeout(timer))
        this.categoryFlashTimers = {}
        this.flashingCategoryIndices = {}
      })
    }
  },
});
</script>
<style scoped>
/* Keep fields horizontally aligned across a row even when some labels have
   the "Learn More" info icon and some don't. Instead of pushing whole
   label+input blocks to the bottom of the row (which mis-aligns the labels
   themselves), give every label a fixed reserved height so the input/select
   controls that follow always start at the same vertical position. */
:deep(.row) > [class*="col-"] {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

:deep(.bp-label) {
  display: flex;
  align-items: flex-end;
  min-height: 34px;
  margin: 0;
  font-weight: 600;
}

:deep(.bp-form-item) {
  display: flex;
  flex-direction: column;
}

/* The blueprint-ui.css library sets .bp-form-label { margin: 0 0 12px 0 }
   by default. Re-declare the same margin explicitly here (instead of relying
   on the library default) so it can't drift out of sync with .bp-label
   above, which is what was causing a 1-4px vertical offset between
   fg-input fields and el-select/el-date-picker fields in the same row. */
:deep(.bp-form-item) > .bp-form-label {
  display: flex;
  align-items: flex-end;
  min-height: 34px;
  margin: 0 0 12px 0;
}

/* Match element-plus select/date-picker inputs to the height/border/background of .bp-form-input
   (used by fg-input), so all form controls on this screen look consistent. */
:deep(.el-select),
:deep(.el-date-editor.el-input) {
  width: 100%;
}

:deep(.el-select__wrapper),
:deep(.el-input__wrapper) {
  min-height: 42px;
  padding: 8px 12px;
  border-radius: 4px;
  box-shadow: none !important;
  border: 1px solid rgba(0, 0, 0, .4);
  background-color: #fff;
}

:deep(.el-select__wrapper.is-hovering:not(.is-focused)),
:deep(.el-input__wrapper:hover) {
  border-color: rgba(0, 0, 0, .4);
}

:deep(.el-select__wrapper.is-focused),
:deep(.el-input__wrapper.is-focus) {
  border-color: #1887cc;
  box-shadow: 0 0 0 1px rgba(24, 135, 204, .4) !important;
}

:deep(.el-select__selection),
:deep(.el-input__inner) {
  font-size: 1rem;
  line-height: 24px;
  height: 24px;
  color: #121212;
}

:deep(.el-select__placeholder),
:deep(.el-input__inner::placeholder) {
  color: rgba(0, 0, 0, .75);
}


.se-earnings-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding-right: 12px;
}

.se-earnings-title-block {
  flex: 1 1 auto;
  text-align: left;
  line-height: 1.3;
}

.se-panel-title {
  font-weight: 600;
  margin-bottom: 2px;
}

.se-panel-subtitle {
  font-weight: 400;
  color: rgba(0, 0, 0, .6);
  font-size: .875rem;
  margin-top: 0;
}

.se-earnings-status {
  flex: 0 0 auto;
  font-weight: 600;
  margin-right: 12px;
}

.se-earnings-delete-btn {
  flex: 0 0 auto;
  margin-right: 16px;
  white-space: nowrap;
  padding: 2px 10px;
  font-size: .8rem;
  line-height: 1.4;
  min-height: auto;
}

.se-earnings-status--complete {
  color: #1a7d3a;
}

.se-earnings-status--incomplete {
  color: rgba(0, 0, 0, .5);
}

:deep(.el-collapse-item__header) {
  height: auto;
  min-height: 48px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.se-earnings-add-row {
  margin-top: 16px;
}

.se-not-on-paystub-cb {
  margin-top: 6px;
  white-space: normal;
}

:deep(.se-not-on-paystub-cb .el-checkbox__label) {
  font-size: .8rem;
  line-height: 1.2;
  white-space: normal;
}

.se-readonly-input {
  background-color: #ebebeb !important;
  color: rgba(0, 0, 0, .6) !important;
  cursor: not-allowed;
}

/* Highlight required paystub header fields that are missing a value after
   a failed Continue validation. */
.se-field-missing :deep(.bp-label) {
  color: #c0392b;
}

.se-field-missing :deep(.el-select__wrapper),
.se-field-missing :deep(.el-input__wrapper) {
  border-color: #c0392b !important;
  box-shadow: 0 0 0 1px rgba(192, 57, 43, .3) !important;
}

/* "Category auto-set by description lookup" flash effect: a quick, friendly glow/pulse
   drawing the eye to the Category dropdown right after its value was populated for the
   user, without being so persistent that it's distracting afterward. */
.se-category-flash :deep(.el-select__wrapper) {
  animation: se-category-flash-pulse 1.4s ease-out;
}

@keyframes se-category-flash-pulse {
  0% {
    border-color: #1a7d3a;
    background-color: #e6f6ea;
    box-shadow: 0 0 0 4px rgba(26, 125, 58, .25) !important;
  }
  60% {
    border-color: #1a7d3a;
    background-color: #e6f6ea;
    box-shadow: 0 0 0 4px rgba(26, 125, 58, .12) !important;
  }
  100% {
    border-color: rgba(0, 0, 0, .4);
    background-color: #fff;
    box-shadow: none !important;
  }
}
</style>


